/**
 * The professions as illustrated cards — w12b (C11, FR-A9 *"Profession
 * (four illustrated cards)"*, FR-A8 §9); w13b adds the fifth, *Other*
 * (ADR-0031), which spans the row and asks *"What do you do?"* under it.
 *
 * One component, drawn twice: onboarding's first step and *Profession &
 * website conduct* in Settings. A radio group underneath (`role="radiogroup"`,
 * arrow keys through the native radios), so choosing a profession is one
 * control with four options rather than four buttons that each save.
 *
 * The pictures are inline SVG in the theme's own tokens — no image files, no
 * raw colours — so they follow the primary colour and stay sharp at any size.
 */

import { Button, Input } from '@fiveox/ui'
import { Check } from 'lucide-react'
import type { ReactNode } from 'react'

import { PROFESSION_LABEL_MAX, type Profession } from '../../../lib/api/firm'

export interface ProfessionMeta {
  code: Profession
  /** One member — *Chartered Accountant*. */
  title: string
  /** FR-I15's default profession line — *Chartered Accountants*. */
  plural: string
  institute: string
  blurb: string
}

export const PROFESSIONS: ProfessionMeta[] = [
  {
    code: 'ca',
    title: 'Chartered Accountant',
    plural: 'Chartered Accountants',
    institute: 'ICAI',
    blurb: 'Audit, tax, GST and accounting for businesses and individuals.',
  },
  {
    code: 'cs',
    title: 'Company Secretary',
    plural: 'Company Secretaries',
    institute: 'ICSI',
    blurb: 'Corporate law, ROC filings, board and secretarial compliance.',
  },
  {
    code: 'cma',
    title: 'Cost & Management Accountant',
    plural: 'Cost Accountants',
    institute: 'ICMAI',
    blurb: 'Cost audit, cost records and management accounting.',
  },
  {
    code: 'advocate',
    title: 'Advocate',
    plural: 'Advocates',
    institute: 'Bar Council of India',
    blurb: 'Litigation, advisory, drafting and representation.',
  },
  // w13b — ADR-0031 (C12). The owner: *"5th "Other" who have nothing to do
  // comply … professionals like Architect, Investment advisors, designer"*.
  // `plural` is only the fallback: an `other` firm's words are its own
  // (`professionName`).
  {
    code: 'other',
    title: 'Other profession',
    plural: 'Other professionals',
    institute: 'You name it · no conduct policy',
    blurb: 'Architects, investment advisers, designers — any practice. You say what you do, and no institute’s rules are applied.',
  },
]

export function professionMeta(code: string | null | undefined): ProfessionMeta | null {
  return PROFESSIONS.find((p) => p.code === code) ?? null
}

/** The profession as the firm's website names it — one of the four's own
 *  plurals, or, for `other`, the firm's own words (ADR-0031 §3). */
export function professionName(
  code: string | null | undefined,
  label?: string | null,
): string | null {
  if (code === 'other') return label?.trim() || 'Other professionals'
  return professionMeta(code)?.plural ?? null
}

/** Starting points for *"What do you do?"* — the ADR's own examples. A tap
 *  fills the box; the words are still the firm's to change. */
export const OTHER_EXAMPLES = ['Architects', 'Investment Advisers', 'Interior Designers', 'Designers']

// -- the pictures -------------------------------------------------------------

function Frame({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 160 96" className="h-full w-full" aria-hidden focusable="false">
      <circle cx="132" cy="18" r="30" className="fill-primary/10" />
      <circle cx="22" cy="84" r="22" className="fill-primary/10" />
      <circle cx="146" cy="78" r="4" className="fill-primary/30" />
      <circle cx="16" cy="18" r="3" className="fill-primary/30" />
      {children}
    </svg>
  )
}

/** A ledger with ticked lines and a stack of coins. */
function LedgerArt() {
  return (
    <Frame>
      <rect x="40" y="16" width="62" height="68" rx="6" className="fill-card stroke-primary" strokeWidth="2" />
      <rect x="40" y="16" width="12" height="68" rx="4" className="fill-primary/20" />
      {[32, 44, 56, 68].map((y) => (
        <g key={y}>
          <path d={`M60 ${y}h28`} className="stroke-primary/40" strokeWidth="3" strokeLinecap="round" />
          <path d={`M90 ${y - 1}l2 2 4-4`} className="stroke-primary" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      ))}
      {[74, 67, 60].map((y) => (
        <ellipse key={y} cx="120" cy={y} rx="14" ry="5" className="fill-primary/25 stroke-primary" strokeWidth="1.5" />
      ))}
      <text x="120" y="62" textAnchor="middle" className="fill-primary text-[7px] font-bold">₹</text>
    </Frame>
  )
}

/** A resolution with a seal and a ribbon. */
function SealArt() {
  return (
    <Frame>
      <rect x="36" y="12" width="60" height="74" rx="6" className="fill-card stroke-primary" strokeWidth="2" />
      {[26, 36, 46].map((y) => (
        <path key={y} d={`M46 ${y}h40`} className="stroke-primary/40" strokeWidth="3" strokeLinecap="round" />
      ))}
      <path d="M46 56h24" className="stroke-primary/40" strokeWidth="3" strokeLinecap="round" />
      <path d="M104 70l-6 18 8-4 6 6 2-18z" className="fill-primary/40" />
      <path d="M118 70l6 18-8-4-6 6-2-18z" className="fill-primary/30" />
      <circle cx="111" cy="60" r="16" className="fill-primary/20 stroke-primary" strokeWidth="2" />
      <circle cx="111" cy="60" r="9" className="fill-card stroke-primary" strokeWidth="1.5" />
      <path d="M107 60l3 3 5-6" className="stroke-primary" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </Frame>
  )
}

/** Cost bars under a gauge. */
function GaugeArt() {
  return (
    <Frame>
      <path d="M36 84h92" className="stroke-primary/40" strokeWidth="2" strokeLinecap="round" />
      {[
        [44, 56],
        [60, 44],
        [76, 62],
        [92, 36],
      ].map(([x, y]) => (
        <rect key={x} x={x} y={y} width="11" height={84 - y!} rx="3" className="fill-primary/25 stroke-primary" strokeWidth="1.5" />
      ))}
      <path d="M104 44a18 18 0 0 1 36 0" className="stroke-primary/30" strokeWidth="6" fill="none" strokeLinecap="round" />
      <path d="M104 44a18 18 0 0 1 24-17" className="stroke-primary" strokeWidth="6" fill="none" strokeLinecap="round" />
      <path d="M122 44l8-10" className="stroke-primary" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="122" cy="44" r="3" className="fill-primary" />
    </Frame>
  )
}

/** The scales, on a plinth. */
function ScalesArt() {
  return (
    <Frame>
      <path d="M80 18v58" className="stroke-primary" strokeWidth="3" strokeLinecap="round" />
      <path d="M50 28h60" className="stroke-primary" strokeWidth="3" strokeLinecap="round" />
      <circle cx="80" cy="18" r="4" className="fill-primary" />
      <path d="M50 28l-12 26M50 28l12 26M110 28l-12 26M110 28l12 26" className="stroke-primary/50" strokeWidth="1.5" />
      <path d="M36 54a14 7 0 0 0 28 0z" className="fill-primary/25 stroke-primary" strokeWidth="2" />
      <path d="M96 54a14 7 0 0 0 28 0z" className="fill-primary/25 stroke-primary" strokeWidth="2" />
      <rect x="62" y="76" width="36" height="8" rx="3" className="fill-primary/30 stroke-primary" strokeWidth="1.5" />
    </Frame>
  )
}

/** w13b — a drawing board: a sheet with a plan on it, a set square, a
 *  compass and a pencil. Any practice that makes something for a client, in
 *  the same strokes and tokens as the four. */
function StudioArt() {
  return (
    <Frame>
      <rect x="30" y="14" width="74" height="66" rx="6" className="fill-card stroke-primary" strokeWidth="2" />
      <path d="M38 24h58M38 72h58" className="stroke-primary/20" strokeWidth="1.5" />
      <path d="M44 30h26v18H44zM70 30h20v30H70zM44 48h26v12H44z" className="fill-primary/10 stroke-primary/60" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M56 48v-5" className="stroke-primary/60" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M40 76l24-24v24z" className="fill-primary/25 stroke-primary" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M118 16l-12 44M118 16l12 44" className="stroke-primary" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="118" cy="16" r="4" className="fill-primary" />
      <path d="M106 60a14 6 0 0 0 24 0" className="stroke-primary/40" strokeWidth="1.5" fill="none" strokeDasharray="3 3" />
      <path d="M134 34l10-10 5 5-10 10-7 2z" className="fill-primary/30 stroke-primary" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M144 24l5 5" className="stroke-primary" strokeWidth="1.5" />
    </Frame>
  )
}

const ART: Record<Profession, () => ReactNode> = {
  ca: LedgerArt,
  cs: SealArt,
  cma: GaugeArt,
  advocate: ScalesArt,
  other: StudioArt,
}

export function ProfessionArt({ code }: { code: Profession }) {
  const Art = ART[code]
  return <Art />
}

// -- the cards ----------------------------------------------------------------

/**
 * *"What do you do?"* — w13b, ADR-0031 §1. Required with `other`; the words
 * become the line under the firm's name (FR-I15). Drawn under the cards when
 * `other` is chosen, and by the settings page's change dialog.
 */
export function OtherLabelField({
  value,
  onChange,
  disabled,
  error,
}: {
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  error?: string | null
}) {
  return (
    <div className="space-y-3" data-other-label="">
      <Input
        label="What do you do?"
        placeholder="Architects"
        value={value}
        maxLength={PROFESSION_LABEL_MAX}
        disabled={disabled}
        error={error}
        onChange={(e) => onChange(e.target.value)}
        hint="In your own words, as your clients would say it. It becomes the line under your firm’s name on your website."
      />
      <div className="flex flex-wrap gap-2" aria-label="Examples">
        {OTHER_EXAMPLES.map((example) => (
          <Button
            key={example}
            type="button"
            size="sm"
            variant={value.trim() === example ? 'primary' : 'outline'}
            disabled={disabled}
            onClick={() => onChange(example)}
          >
            {example}
          </Button>
        ))}
      </div>
    </div>
  )
}

export function ProfessionCards({
  value,
  onChange,
  disabled,
  name = 'profession',
  label = 'Your profession',
  otherLabel,
  onOtherLabelChange,
  otherLabelError,
}: {
  value: Profession | null
  onChange: (next: Profession) => void
  disabled?: boolean
  name?: string
  label?: string
  /** w13b — *"What do you do?"*, shown under the cards while `other` is
   *  chosen. Omit `onOtherLabelChange` and the field is not drawn (the
   *  settings page asks in its dialog instead). */
  otherLabel?: string | null
  onOtherLabelChange?: (next: string) => void
  otherLabelError?: string | null
}) {
  return (
    <div className="space-y-4">
      <div role="radiogroup" aria-label={label} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {PROFESSIONS.map((p) => {
          const chosen = value === p.code
          // The fifth spans the row on a wide screen, picture beside words, so
          // it reads as a choice of its own rather than a card left over.
          const wide = p.code === 'other'
          return (
            <label
              key={p.code}
              data-profession={p.code}
              className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border bg-card text-left transition focus-within:ring-2 focus-within:ring-ring ${
                wide ? 'sm:col-span-2 sm:flex-row' : ''
              } ${
                chosen
                  ? 'border-primary shadow-md ring-1 ring-primary'
                  : 'border-border hover:border-primary/60 hover:shadow-sm'
              } ${disabled ? 'cursor-not-allowed opacity-70' : ''}`}
            >
              <input
                type="radio"
                name={name}
                value={p.code}
                checked={chosen}
                disabled={disabled}
                onChange={() => onChange(p.code)}
                className="sr-only"
              />
              <span
                className={`block h-24 w-full shrink-0 transition ${wide ? 'sm:h-auto sm:min-h-28 sm:w-56' : ''} ${chosen ? 'bg-primary/10' : 'bg-muted/60 group-hover:bg-primary/5'}`}
              >
                <ProfessionArt code={p.code} />
              </span>
              <span className="flex flex-1 flex-col gap-1 p-4">
                <span className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-foreground">{p.title}</span>
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                      chosen ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
                    }`}
                    aria-hidden
                  >
                    {chosen && <Check size={12} strokeWidth={3} />}
                  </span>
                </span>
                <span className="text-xs font-medium uppercase tracking-wide text-primary">
                  {p.institute}
                </span>
                <span className="text-sm text-muted-foreground">{p.blurb}</span>
              </span>
            </label>
          )
        })}
      </div>
      {value === 'other' && onOtherLabelChange && (
        <div className="rounded-2xl border border-primary/40 bg-primary/5 p-4">
          <OtherLabelField
            value={otherLabel ?? ''}
            onChange={onOtherLabelChange}
            disabled={disabled}
            error={otherLabelError}
          />
        </div>
      )}
    </div>
  )
}
